/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 27-07-2026
 *
 * Root-service met de sim-sessiestate die de eilanden delen: of er gesimuleerd wordt, de vier
 * selectievlaggen van de toestandsknoppen, welke histories aanstaan en wat de eerstvolgende
 * simulatiestap is. `DlSimCanvasComponent` schrijft, `dl-sim-toolbar` en de feedback-eilanden
 * lezen — ze staan niet in elkaars boom, dus een root-service is het kanaal (golf 3d, besluit 1;
 * plaatsingsregel 17-07-2026).
 *
 * Sinds golf 3d is deze service ook de eigenaar van `simuleren` (handoff-regel 4); de
 * `SimulatieService` zet hem, het bouwscherm leest hem voor de verdeling van het scherm.
 *
 * Daarnaast draagt de service de transport- en logketens van het simcanvas
 * (`SimulatieActies`): het canvas bezit de state, de samenwerk-baas het simulatiebericht en het
 * actielog. De statusverzending zelf loopt niet hierlangs maar over de baas, omdat die de
 * wire-vorm samenstelt.
 */
import {Injectable, signal} from '@angular/core';
import type {StateSelectieVlaggen, SimStapSoort} from '../components/canvas/dl-simcanvas/dl-simcanvas.component';

/**
 * Welke history-soorten op dit moment getoond worden.
 */
export interface HistoryStand {
    value: boolean;
    ineq: boolean;
}

/**
 * De states die deze client geselecteerd heeft, zoals ze naar de leider gaan: de tellerstand
 * plus per state zijn plek in de selectie- en padvolgorde.
 */
export interface SimSelectieOverdracht {
    selectievolgorde: number;
    selected: Array<{id: string; selectievolgorde: number; padvolgorde: number[] | undefined}>;
}

/**
 * De inhoud van de actielogregel bij een selectiewijziging: bij precies één geselecteerde state
 * de volledige nodedata, anders alleen de ids. De samenwerk-baas kiest op grond hiervan tussen
 * `sim_select` en `sim_multiselect`.
 */
export interface SimSelectieLog {
    enkel: Record<string, unknown> | null;
    ids: string[];
}

/**
 * De ketens die de samenwerk-baas registreert: transport (het simulatiebericht) en actielog (met de
 * snapshot).
 */
export interface SimulatieActies {
    /**
     * Een volger heeft states (de)geselecteerd: het verzoek gaat als `setSelected` naar de
     * leider, die het uitvoert, rondstuurt en op naam van de volger logt.
     */
    vraagSelectie: (selectie: SimSelectieOverdracht) => void;

    /**
     * De selectie is bij leider/lokaal gewijzigd: actielog. `volger` is gevuld als de wijziging
     * uit een volger-verzoek voortkwam.
     */
    meldSelectie: (log: SimSelectieLog, volger?: string) => void;

    /**
     * Er zijn states verplaatst: de samenwerk-baas logt (behalve als volger) en stuurt het
     * `positie`-bericht (behalve lokaal).
     */
    meldPosities: (posities: Record<string, {x: number; y: number}>) => void;

    /**
     * Een volger wil een value-history verbergen; alleen de leider voert dat uit.
     */
    vraagVerbergValuehistory: (historyNodeId: string) => void;

    /**
     * Een volger wil een ineq-history verbergen; alleen de leider voert dat uit.
     */
    vraagVerbergIneqhistory: (ineqkey: string) => void;
}

@Injectable({providedIn: 'root'})
export class SimulatieStatusService {

    /**
     * Of de simulatie draait.
     */
    private readonly _simuleren = signal(false);

    /**
     * De vier selectievlaggen van de toestandsknoppen, berekend door het simcanvas.
     */
    private readonly _stateSelectie = signal<StateSelectieVlaggen>({alles: false, niets: false, endings: false, pad: false});

    /**
     * Welke histories op dit moment getoond worden.
     */
    private readonly _history = signal<HistoryStand>({value: false, ineq: false});

    /**
     * De eerstvolgende stap-soort in de simulatie; bepaalt de knopklasse in de toolbar.
     */
    private readonly _volgendeStap = signal<SimStapSoort>('start');

    /**
     * Of er een simulatie gemaakt wordt voor dit model — bij de uitvoerder én bij zijn volgers. Het
     * simcanvas legt er dan een spinner overheen (golf 3.6).
     */
    private readonly _genereren = signal(false);

    /**
     * De door de samenwerk-baas geregistreerde ketens.
     */
    private acties: SimulatieActies | null = null;

    /**
     * Of de simulatie draait, als readonly signal.
     */
    readonly simuleren = this._simuleren.asReadonly();

    /**
     * De vier selectievlaggen van de toestandsknoppen, als readonly signal.
     */
    readonly stateSelectie = this._stateSelectie.asReadonly();

    /**
     * Welke histories getoond worden, als readonly signal.
     */
    readonly history = this._history.asReadonly();

    /**
     * De eerstvolgende stap-soort in de simulatie, als readonly signal.
     */
    readonly volgendeStap = this._volgendeStap.asReadonly();

    /**
     * Of er een simulatie gemaakt wordt, als readonly signal.
     */
    readonly genereren = this._genereren.asReadonly();

    /**
     * De samenwerk-baas registreert de transport- en logketens.
     */
    initialiseer(acties: SimulatieActies): void {
        this.acties = acties;
    }

    /* ******** De state: geschreven door het simcanvas en de SimulatieService ******** */

    /**
     * Zet of de simulatie draait.
     */
    zetSimuleren(simuleren: boolean): void {
        this._simuleren.set(!!simuleren);
    }

    /**
     * Zet of er een simulatie gemaakt wordt. Geschreven door de `SimulatieService`.
     */
    zetGenereren(genereren: boolean): void {
        this._genereren.set(!!genereren);
    }

    /**
     * Zet de vier selectievlaggen van de toestandsknoppen.
     */
    zetStateSelectie(vlaggen: StateSelectieVlaggen): void {
        this._stateSelectie.set(vlaggen);
    }

    /**
     * Zet welke histories getoond worden.
     */
    zetHistory(stand: HistoryStand): void {
        this._history.set({value: !!stand.value, ineq: !!stand.ineq});
    }

    /**
     * Zet de value-history-vlag; de ineq-vlag blijft zoals hij was.
     */
    zetToonValuehistory(toon: boolean): void {
        this._history.update(stand => ({...stand, value: !!toon}));
    }

    /**
     * Zet de ineq-history-vlag; de value-vlag blijft zoals hij was.
     */
    zetToonIneqhistory(toon: boolean): void {
        this._history.update(stand => ({...stand, ineq: !!toon}));
    }

    /**
     * Zet de eerstvolgende stap-soort in de simulatie.
     */
    zetVolgendeStap(stap: SimStapSoort): void {
        this._volgendeStap.set(stap);
    }

    /* ******** Richting de samenwerk-baas ******** */

    /**
     * Een volger meldt zijn nieuwe stateselectie bij de leider.
     */
    vraagSelectie(selectie: SimSelectieOverdracht): void {
        this.acties?.vraagSelectie(selectie);
    }

    /**
     * Leider/lokaal: log de gewijzigde stateselectie.
     */
    meldSelectie(log: SimSelectieLog, volger?: string): void {
        this.acties?.meldSelectie(log, volger);
    }

    /**
     * Meld verplaatste states: loggen en/of rondsturen doet de samenwerk-baas.
     */
    meldPosities(posities: Record<string, {x: number; y: number}>): void {
        this.acties?.meldPosities(posities);
    }

    /**
     * Een volger vraagt de leider een value-history te verbergen.
     */
    vraagVerbergValuehistory(historyNodeId: string): void {
        this.acties?.vraagVerbergValuehistory(historyNodeId);
    }

    /**
     * Een volger vraagt de leider een ineq-history te verbergen.
     */
    vraagVerbergIneqhistory(ineqkey: string): void {
        this.acties?.vraagVerbergIneqhistory(ineqkey);
    }
}
